import React, { FC, useState, useMemo, useCallback, memo } from "react";
import dayjs, { Dayjs } from "dayjs";
import { useIntl } from "react-intl";
import { AlertCircle } from "lucide-react";

import { DayIntervals, Interval, ActivityType } from "../../../../../model/activity.model";
import { activityStore } from "../../../../../store/activity.store";
import convertDecimalToHexColor from "../../../../../utils/convertDecimalToHexColor";
import { getDuration } from "./helper";
import IntervalPopoverMenu from "./components/IntervalPopoverMenu";
import style from './style.module.css';
import getTypeFullPath from "../../../../../utils/getTypeFullPath";
import { authStore } from "../../../../../store/auth.store";
import { List } from "../../../../ui/list";
import { Badge } from "../../../../ui/badge";
import { TimeRangePicker } from "../../../../ui/time-range-picker";
import { SingleTimePicker } from "../../../../ui/single-time-picker";
import { Card, CardContent } from "../../../../ui/card";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../../../../ui/alert-dialog";
import { structureIntervalsWithOccurrences, StructuredInterval } from "../../../../../utils/occurrenceUtils";

interface IDaysIntervals {
  dayIntervals: DayIntervals
}

// Memoized interval item to prevent unnecessary re-renders
interface IntervalItemProps {
  interval: Interval;
  activityType: ActivityType | undefined;
  activityTypes: ActivityType[] | null;
  fullPath: boolean | undefined;
  onEdit: (activityId: string) => void;
  onDelete: (interval: Interval) => void;
  onChangeInterval: (interval: Interval, dates: [Dayjs, Dayjs]) => void;
}

const IntervalItem = memo<IntervalItemProps>(({
  interval,
  activityType,
  activityTypes,
  fullPath,
  onEdit,
  onDelete,
  onChangeInterval,
}) => {
  const name = fullPath ? getTypeFullPath(activityType, activityTypes) : activityType?.name;

  // Memoize dayjs values
  const timeValue = useMemo<[Dayjs, Dayjs]>(() => [
    dayjs(interval.start),
    dayjs(interval.finish)
  ], [interval.start, interval.finish]);

  const handleEdit = useCallback(() => {
    onEdit(interval.activityId);
  }, [onEdit, interval.activityId]);

  const handleDelete = useCallback(() => {
    onDelete(interval);
  }, [onDelete, interval]);

  const handleChangeInterval = useCallback((dates: [Dayjs, Dayjs] | null) => {
    if (dates && dates[0] && dates[1]) {
      onChangeInterval(interval, dates);
    }
  }, [onChangeInterval, interval]);

  return (
    <List.Item style={{ padding: '4px 0' }} className="border-b border-border/50 last:border-b-0">
      <div className={style.IntervalContainer}>
        {/* Desktop layout */}
        <div className="hidden sm:flex items-center gap-2">
          {/* Left side - Activity info */}
          <div className="flex items-center gap-2 flex-shrink-0">
            <div
              style={{
                background: convertDecimalToHexColor(activityType?.color || 0)
              }}
              className={style.ActivityTypesFilterLabelColor}
            />
            <span className={style.ActivityTypeName}>{name}</span>
          </div>

          {/* Right side - Tags, Time and actions */}
          <div className="flex items-center gap-2 ml-auto flex-shrink-0">
            {interval.tags.length > 0 && (
              <div className="flex gap-1">
                {interval.tags.map(tag => (
                  <Badge key={tag} variant="secondary">{tag}</Badge>
                ))}
              </div>
            )}
            <div className={style.IntervalRangeLabel}>
              <TimeRangePicker
                value={timeValue}
                onChange={handleChangeInterval}
              />
            </div>
            <div className={style.DurationLabel}>{getDuration(interval)}</div>

            <IntervalPopoverMenu
              setActivityToEdit={handleEdit}
              setIntervalToDelete={handleDelete}
            />
          </div>
        </div>

        {/* Mobile layout */}
        <div className="sm:hidden flex gap-3">
          {/* Left side: Activity info */}
          <div className="flex-1 min-w-0 space-y-1">
            {/* Row 1: Activity name (clickable to edit) */}
            <div className="flex items-center gap-2">
              <div
                style={{
                  background: convertDecimalToHexColor(activityType?.color || 0)
                }}
                className={style.ActivityTypesFilterLabelColor}
              />
              <span
                className={`${style.ActivityTypeName} cursor-pointer hover:opacity-70 truncate`}
                onClick={handleEdit}
              >
                {name}
              </span>
            </div>

            {/* Row 2: Time range */}
            <div>
              <TimeRangePicker
                value={timeValue}
                onChange={handleChangeInterval}
              />
            </div>

            {/* Row 3: Tags */}
            {interval.tags.length > 0 && (
              <div className="flex gap-1 flex-wrap">
                {interval.tags.map(tag => (
                  <Badge key={tag} variant="secondary">{tag}</Badge>
                ))}
              </div>
            )}

            {/* Row 4: Comment */}
            {interval.comment && (
              <div className="text-sm text-muted-foreground">
                {interval.comment}
              </div>
            )}
          </div>

          {/* Right side: Duration */}
          <div className="flex-shrink-0 flex items-center">
            <span className="text-xl font-semibold tabular-nums text-foreground">
              {getDuration(interval)}
            </span>
          </div>
        </div>

        {/* Comment - desktop only */}
        <div className="hidden sm:block">
          {interval.comment && (
            <div className="text-sm text-muted-foreground mt-1">
              {interval.comment}
            </div>
          )}
        </div>
      </div>
    </List.Item>
  );
});

// Occurrence item — used for both standalone and nested occurrences
interface OccurrenceItemProps {
  interval: Interval;
  activityType: ActivityType | undefined;
  activityTypes: ActivityType[] | null;
  fullPath: boolean | undefined;
  isNested?: boolean;
  onEdit: (activityId: string) => void;
  onDelete: (interval: Interval) => void;
  onChangeInterval: (interval: Interval, dates: [Dayjs, Dayjs]) => void;
}

const OccurrenceItem = memo<OccurrenceItemProps>(({
  interval,
  activityType,
  activityTypes,
  fullPath,
  isNested = false,
  onEdit,
  onDelete,
  onChangeInterval,
}) => {
  const name = fullPath ? getTypeFullPath(activityType, activityTypes) : activityType?.name;
  const color = convertDecimalToHexColor(activityType?.color || 0);

  const timeValue = useMemo(() => dayjs(interval.start), [interval.start]);

  const handleEdit = useCallback(() => {
    onEdit(interval.activityId);
  }, [onEdit, interval.activityId]);

  const handleDelete = useCallback(() => {
    onDelete(interval);
  }, [onDelete, interval]);

  const handleChangeTime = useCallback((newTime: Dayjs | null) => {
    if (newTime) {
      onChangeInterval(interval, [newTime, newTime]);
    }
  }, [onChangeInterval, interval]);

  const content = (
    <div className={style.IntervalContainer}>
      {/* Desktop layout */}
      <div className="hidden sm:flex items-center gap-2">
        <div className="flex items-center gap-2 flex-shrink-0">
          <div
            style={{ background: color }}
            className={style.ActivityTypesFilterLabelColor}
          />
          <span className={style.ActivityTypeName}>{name}</span>
        </div>

        <div className="flex items-center gap-2 ml-auto flex-shrink-0">
          {interval.tags.length > 0 && (
            <div className="flex gap-1">
              {interval.tags.map(tag => (
                <Badge key={tag} variant="secondary">{tag}</Badge>
              ))}
            </div>
          )}
          <div className={style.IntervalRangeLabel}>
            <SingleTimePicker
              value={timeValue}
              onChange={handleChangeTime}
            />
          </div>
          <div className={style.DurationLabel}>--:--</div>

          <IntervalPopoverMenu
            setActivityToEdit={handleEdit}
            setIntervalToDelete={handleDelete}
          />
        </div>
      </div>

      {/* Mobile layout */}
      <div className="sm:hidden flex gap-3">
        <div className="flex-1 min-w-0 space-y-1">
          <div className="flex items-center gap-2">
            <div
              style={{ background: color }}
              className={style.ActivityTypesFilterLabelColor}
            />
            <span
              className={`${style.ActivityTypeName} cursor-pointer hover:opacity-70 truncate`}
              onClick={handleEdit}
            >
              {name}
            </span>
          </div>
          <div>
            <SingleTimePicker
              value={timeValue}
              onChange={handleChangeTime}
            />
          </div>
          {interval.tags.length > 0 && (
            <div className="flex gap-1 flex-wrap">
              {interval.tags.map(tag => (
                <Badge key={tag} variant="secondary">{tag}</Badge>
              ))}
            </div>
          )}
          {interval.comment && (
            <div className="text-sm text-muted-foreground">
              {interval.comment}
            </div>
          )}
        </div>
        <div className="flex-shrink-0 flex items-center">
          <span className="text-xl font-semibold tabular-nums text-muted-foreground">
            --:--
          </span>
        </div>
      </div>

      {/* Comment - desktop only */}
      <div className="hidden sm:block">
        {interval.comment && (
          <div className="text-sm text-muted-foreground mt-1">
            {interval.comment}
          </div>
        )}
      </div>
    </div>
  );

  if (isNested) {
    return (
      <li style={{ padding: '4px 0', listStyle: 'none' }} className="border-b border-border/50 last:border-b-0">
        <div className={style.NestedOccurrenceWrapper}>
          <div className={style.NestedVerticalLine} />
          <div className={style.NestedContent}>{content}</div>
        </div>
      </li>
    );
  }

  return (
    <li style={{ padding: '4px 0', listStyle: 'none' }} className="border-b border-border/50 last:border-b-0">
      {content}
    </li>
  );
});

const DaysIntervals: FC<IDaysIntervals> = memo(({ dayIntervals }) => {
  const { formatMessage } = useIntl();
  const activityTypes = activityStore(state => state.activityTypes);
  const setActivityToEdit = activityStore(state => state.setActivityToEdit);
  const getActivityDetails = activityStore(state => state.getActivityDetails);
  const deleteInterval = activityStore(state => state.deleteInterval);
  const rewriteInterval = activityStore(state => state.rewriteInterval);
  const fullPath = authStore(state => state.user?.settings.fullPath);

  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [intervalToDelete, setIntervalToDelete] = useState<Interval | null>(null);

  // O(1) lookup map for activity types
  const activityTypesMap = useMemo(() =>
    new Map(activityTypes?.map(t => [t.id, t]) || []),
    [activityTypes]
  );

  // Structure intervals with occurrence nesting
  const structuredIntervals = useMemo(() =>
    structureIntervalsWithOccurrences(dayIntervals.intervals, activityTypesMap),
    [dayIntervals.intervals, activityTypesMap]
  );

  const handleConfirmDelete = useCallback(() => {
    if (intervalToDelete) {
      deleteInterval(intervalToDelete);
      setIntervalToDelete(null);
    }
    setDeleteDialogOpen(false);
  }, [intervalToDelete, deleteInterval]);

  // Stable callback for editing
  const handleEdit = useCallback((activityId: string) => {
    getActivityDetails(activityId, setActivityToEdit);
  }, [getActivityDetails, setActivityToEdit]);

  // Stable callback for deleting
  const handleDelete = useCallback((interval: Interval) => {
    setIntervalToDelete(interval);
    setDeleteDialogOpen(true);
  }, []);

  // Stable callback for changing interval
  const handleChangeInterval = useCallback((interval: Interval, dates: [Dayjs, Dayjs]) => {
    const newInterval: Interval = {
      ...interval,
      start: dates[0].toISOString(),
      finish: dates[1].toISOString()
    };
    void rewriteInterval(newInterval);
  }, [rewriteInterval]);

  return (
    <>
      <div className="mb-3">
        <h3 className="text-sm font-medium text-muted-foreground mb-1.5 px-1">{dayIntervals.title}</h3>
        <Card>
          <CardContent className="px-4 py-2">
            <ul className={style.DaysIntervals}>
              {structuredIntervals.map((item: StructuredInterval) => {
                if (item.isOccurrence) {
                  // Standalone occurrence
                  return (
                    <OccurrenceItem
                      key={item.interval.id}
                      interval={item.interval}
                      activityType={activityTypesMap.get(item.interval.typeId)}
                      activityTypes={activityTypes}
                      fullPath={fullPath}
                      onEdit={handleEdit}
                      onDelete={handleDelete}
                      onChangeInterval={handleChangeInterval}
                    />
                  );
                }

                // Regular interval + nested occurrences
                return (
                  <React.Fragment key={item.interval.id}>
                    <IntervalItem
                      interval={item.interval}
                      activityType={activityTypesMap.get(item.interval.typeId)}
                      activityTypes={activityTypes}
                      fullPath={fullPath}
                      onEdit={handleEdit}
                      onDelete={handleDelete}
                      onChangeInterval={handleChangeInterval}
                    />
                    {item.nestedOccurrences.map(occ => (
                      <OccurrenceItem
                        key={occ.id}
                        interval={occ}
                        activityType={activityTypesMap.get(occ.typeId)}
                        activityTypes={activityTypes}
                        fullPath={fullPath}
                        isNested
                        onEdit={handleEdit}
                        onDelete={handleDelete}
                        onChangeInterval={handleChangeInterval}
                      />
                    ))}
                  </React.Fragment>
                );
              })}
            </ul>
          </CardContent>
        </Card>
      </div>

      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <AlertCircle className="h-5 w-5 text-destructive" />
              {formatMessage({ id: 'DELETE_INTERVAL' })}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {formatMessage({ id: 'DELETE_INTERVAL_CONFIRM' })}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{formatMessage({ id: 'NO' })}</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleConfirmDelete}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {formatMessage({ id: 'DELETE' })}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
});

export default DaysIntervals;
